import React, { useState } from "react";
import { AnswersType, FlagsType, ParticipantExamQuestion } from "../../../../types/question";
import { useTranslation } from "react-i18next";
import { TRANSLATION_CONFIG } from "../../../../utils/consts/translation";
import { Header } from "./Header";
import { ToggleButton } from "../../../atom/ToggleButton";
import { QuestionItemSummary } from "./QuestionItemSummary";
import { SummaryFilterType, SummaryStateType } from "../../../../types/components";
import { Button } from "../../../atom/Button";
import { Modal } from "../../../atom/Modal";
import { usePutResource } from "../../../../services";
import { PlayFilledAlt, WarningAltFilled } from "@carbon/icons-react";
import { useUserContext } from "../../../../utils/contexts/UserProvider";

interface SummaryProps {
  id?: string;
  examId?: string;
  questions: ParticipantExamQuestion[];
  answers: AnswersType;
  flags: FlagsType;
  setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSummary: React.Dispatch<React.SetStateAction<SummaryStateType>>;
  isSummary: SummaryStateType;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<number>>;
  isPractice: boolean;
}

export const Summary = ({
  id,
  examId,
  questions,
  answers,
  flags,
  isSummary,
  setIsSummary,
  setIsCompleted,
  setCurrentQuestion,
  isPractice,
}: SummaryProps) => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const [filter, setFilter] = useState<SummaryFilterType>(isSummary?.filter || "all");
  const [isModalOpen, setModalOpen] = useState(false);
  const { user } = useUserContext();

  const { updateResource } = usePutResource(`/common/participant-exam/${id}`);

  const filteredQuestions = questions
    .map((question, index) => {
      const isAnswered = Boolean(answers[question.id]);
      const isFlagged = Boolean(flags[question.id]);
      return {
        ...question,
        originalIndex: index,
        isAnswered,
        isFlagged,
      };
    })
    .filter(({ isAnswered, isFlagged }) => {
      if (filter === "answered") return isAnswered;
      if (filter === "unanswered") return !isAnswered;
      if (filter === "flagged") return isFlagged;
      return true;
    });

  const confirmFinish = async () => {
    setModalOpen(false);

    updateResource({
      id,
      is_completed: true,
      exam: { id: examId },
      participant: user,
    });

    setIsCompleted(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleFinishClick = () => {
    setModalOpen(true);
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="w-full bg-white">
        <Header
          title={t("Progress summary")}
          showTimer={!isPractice}
          setIsCompleted={setIsCompleted}
        />
        <div className="w-full flex justify-start items-center gap-x-2 px-10 pt-6 pb-5 border-b border-b-gray-200 h-[78px]">
          <ToggleButton
            label={t("ALL")}
            isActive={filter === "all"}
            onClick={() => setFilter("all")}
          />
          <ToggleButton
            label={t("ANSWERED")}
            isActive={filter === "answered"}
            onClick={() => setFilter("answered")}
          />
          <ToggleButton
            label={t("UNANSWERED")}
            isActive={filter === "unanswered"}
            onClick={() => setFilter("unanswered")}
          />
          <ToggleButton
            label={t("FLAGGED")}
            isActive={filter === "flagged"}
            onClick={() => setFilter("flagged")}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar pt-3">
        <ul className="space-y-[1px]">
          {filteredQuestions.map((question) => (
            <QuestionItemSummary
              key={question.id}
              index={question.originalIndex}
              question={question}
              questions={questions}
              flags={flags}
              answers={answers}
              setCurrentQuestion={setCurrentQuestion}
              setIsSummary={setIsSummary}
            />
          ))}
        </ul>
      </div>
      <div className="flex justify-end gap-x-4 items-center border-t border-gray-200 px-7 pt-7 pb-8 h-[104px]">
        <Button
          type="secondary"
          label={t("Resume exam")}
          leftIcon={<PlayFilledAlt size={20} />}
          onClick={() => setIsSummary((prevState) => ({ ...prevState, state: false }))}
          additionalClassName="max-w-fit"
        />
        <Button
          type="primary"
          label={t("Submit all and finish")}
          onClick={handleFinishClick}
          additionalClassName="max-w-fit"
        />
      </div>
      <Modal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        secondButtonClick={confirmFinish}
        title={t("Submit all and finish")}
        subtitle={t(
          "Once submitted, you won’t be able to make any further changes or review your answers. Please ensure you’ve completed all questions to the best of your ability before confirming. Your results will be processed and made available as per the exam guidelines.",
        )}
        firstButtonLabel={t("Go back")}
        secondButtonLabel={t("Confirm")}
        titleIcon={<WarningAltFilled size={31} className="text-yellow-500" />}
      />
    </div>
  );
};
